import { useCallback, useEffect, useState } from 'react';
import { migrateLegacyKey, readStoredObject, storageKey, writeStored } from '@/lib/storage';

/**
 * How wide the debug drawer is. A tester reading a trace wants the log wide; a tester watching the
 * conversation wants it narrow, so neither can be the one built-in answer. Kept out of the session
 * store: it is the shape of the panel, not anything about the conversation.
 */

const STORE_KEY = storageKey('drawer');

/** What builds before the namespaced keys wrote. Read once, so a chosen width survives. */
const LEGACY_STORE_KEY = 'voice-console.drawer';

/** Below this the event rows lose their columns and the readouts wrap. */
const MIN_WIDTH = 380;
/** Left for the conversation, so the drawer cannot be dragged over the whole window. */
const MIN_APP_WIDTH = 320;

const DEFAULT_WIDTH = 580;

/**
 * Clamps a proposed drawer width.
 *
 * @param px the width being dragged to
 * @param viewport the window width
 * @returns a width that leaves both panes usable
 */
export function clampWidth(px: number, viewport: number): number {
  const widest = Math.max(MIN_WIDTH, viewport - MIN_APP_WIDTH);
  return Math.round(Math.min(Math.max(px, MIN_WIDTH), widest));
}

/** The drawer's width and the handles that change it. */
export function useDrawerLayout() {
  const [width, setWidth] = useState(readStoredWidth);
  const [dragging, setDragging] = useState<'width' | null>(null);

  useEffect(() => {
    writeStored(STORE_KEY, { width });
  }, [width]);

  const resizeWidth = useCallback((px: number) => {
    setWidth((current) => {
      const next = clampWidth(px, window.innerWidth);
      return current === next ? current : next;
    });
  }, []);

  // A size chosen on a bigger window, or kept while this one shrinks, would otherwise leave the log
  // or the conversation with nothing to show; re-clamping against the window as it is now pulls the
  // edge back in.
  useEffect(() => {
    const reclamp = () => resizeWidth(width);
    reclamp();
    window.addEventListener('resize', reclamp);
    return () => window.removeEventListener('resize', reclamp);
  }, [width, resizeWidth]);

  /** Follows the pointer until it is released, wherever on the page it goes. */
  const dragWidth = useCallback(() => {
    setDragging('width');
    // For the length of the drag the whole page is the divider: a gesture that crosses the
    // transcript would otherwise select it, and the cursor would flicker on the way past.
    const { body } = document;
    body.style.userSelect = 'none';
    body.style.cursor = 'col-resize';

    /** The drawer's right edge is the window's, so the pointer's distance from it is the width. */
    const onMove = (event: PointerEvent) => resizeWidth(window.innerWidth - event.clientX);
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      body.style.userSelect = '';
      body.style.cursor = '';
      setDragging(null);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }, [resizeWidth]);

  return {
    width,
    dragging,
    dragWidth,
    nudgeWidth: (by: number) => resizeWidth(width + by),
  };
}

function readStoredWidth(): number {
  migrateLegacyKey(LEGACY_STORE_KEY, STORE_KEY);
  const stored = readStoredObject(STORE_KEY);
  // Taken as written; clamped against this window once the panel is laid out.
  return typeof stored?.width === 'number' ? stored.width : DEFAULT_WIDTH;
}
